import React, { useEffect, useState } from "react";
import { fmtTime, fmtRange } from "@/lib/enrol";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, API, errMsg, cleanPhone } from "@/lib/auth";
import { PageHeader } from "@/pages/Classes";
import { toast } from "sonner";
import { Clock, MapPin, User, Calendar, CalendarPlus } from "lucide-react";

const STATUS_LABEL = { available: "AVAILABLE", filling_fast: "FILLING FAST", almost_full: "ALMOST FULL", full: "FULL" };
const STATUS_CLASS = { available: "status-available", filling_fast: "status-filling", almost_full: "status-almost", full: "status-full" };

const fmt = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

function pickCurrentAndNextTerm(terms) {
  if (!terms || terms.length === 0) return { current: null, next: null };
  const today = new Date().toISOString().slice(0, 10);
  const active = terms.filter(t => t.active !== false);
  const sorted = [...active].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
  const current = sorted.find(t => (t.start_date || "") <= today && today <= (t.end_date || "9999-12-31")) || null;
  const next = sorted.find(t => (t.start_date || "") > today) || null;
  return { current, next };
}

export default function BatchDetail() {
  const { batchId } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const trialMode = sp.get("trial") === "1";
  const [batch, setBatch] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [preOpen, setPreOpen] = useState(null);
  const [trialOpen, setTrialOpen] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", age: "", preferred_day: "", notes: "" });
  const [trialForm, setTrialForm] = useState({ full_name: "", email: "", phone: "", age: "", parent_name: "", notes: "" });

  useEffect(() => {
    api.get("/batches").then(r => setBatch(r.data.find(b => b.id === batchId)));
    api.get(`/sessions?batch_id=${batchId}`).then(r => setSessions(r.data));
    api.get("/terms").then(r => setTerms(r.data || []));
  }, [batchId]);

  const submitPreReg = async () => {
    try {
      await api.post("/pre-registrations", { ...form, batch_id: batchId, age: form.age ? parseInt(form.age) : null });
      toast.success("Pre-registration received — we'll be in touch.");
      setPreOpen(null); setForm({ full_name: "", email: "", phone: "", age: "", preferred_day: "", notes: "" });
    } catch (e) { toast.error(errMsg(e, "Failed. Please try again.")); }
  };

  const submitTrial = async () => {
    try {
      await api.post("/free-trial-registrations", { session_id: trialOpen.id, ...trialForm, age: trialForm.age ? parseInt(trialForm.age) : null });
      toast.success("Your free trial inquiry has been submitted — check your email.");
      setTrialOpen(null); setTrialForm({ full_name: "", email: "", phone: "", age: "", parent_name: "", notes: "" });
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  if (!batch) return <div className="pt-40 text-center text-zinc-500">Loading…</div>;

  const competition = batch.audition_only;
  const trialOn = batch.free_trial_enabled !== false && !competition;  // free trial is ON unless an admin switches it off
  const { current: currentTerm, next: nextTerm } = pickCurrentAndNextTerm(terms);
  const today = new Date().toISOString().slice(0, 10);
  const hasSessions = (t) => sessions.some(s => s.term_id === t.id);
  const enrolTerm = (currentTerm && hasSessions(currentTerm)) ? currentTerm
    : terms.filter(t => t.active !== false && (t.end_date || "9999") >= today && hasSessions(t))
        .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""))[0] || currentTerm || nextTerm;
  const enrolSessions = enrolTerm ? sessions.filter(s => !s.term_id || s.term_id === enrolTerm.id) : sessions;
  const laterSessions = enrolTerm ? sessions.filter(s => s.term_id && s.term_id !== enrolTerm.id && terms.some(t => t.id === s.term_id && (t.start_date || "") > (enrolTerm.start_date || ""))) : [];
  const termName = (id) => terms.find(t => t.id === id)?.name || "Upcoming term";
  const firsts = enrolSessions.map(s => s.schedule?.first_class).filter(Boolean).sort();
  const lasts = enrolSessions.map(s => s.schedule?.last_class).filter(Boolean).sort();
  const nClasses = enrolSessions[0]?.schedule?.classes_count || enrolTerm?.classes_count;
  const nextShown = enrolTerm && enrolTerm.id !== currentTerm?.id ? enrolTerm : nextTerm;
  const enrolMeta = enrolTerm && nClasses ? `${nClasses} classes · first class ${fmt(firsts[0] || enrolTerm.start_date)} · last class ${fmt(lasts[lasts.length - 1] || enrolTerm.end_date)}` : "";

  return (
    <>
      <PageHeader eyebrow={batch.age_range} title={batch.name} sub={batch.description} back backTo="/classes" backLabel="Back to classes" />
      {trialOn && (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 -mt-6">
          <div className="bg-red-600/10 border border-red-600/40 px-6 py-4 flex flex-wrap items-center justify-between gap-4" data-testid="free-trial-banner">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🎁</span>
              <div>
                <div className="font-bold uppercase tracking-widest text-xs text-red-500">Free first class</div>
                <div className="text-sm text-zinc-300">{trialMode ? "Pick a class below and tap “Book free trial” — no payment needed." : "Book a no-commitment trial before your term registration."}</div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16">
        {!competition && (currentTerm || nextTerm) && (
          <div className="grid md:grid-cols-2 gap-4 mb-10" data-testid="term-dates">
            <div className="card-surface p-6 border-l-4 border-red-600">
              <div className="flex items-center gap-2 eyebrow text-red-500 mb-3"><Calendar size={14}/> Current term</div>
              {currentTerm ? (
                <>
                  <div className="font-display text-2xl uppercase leading-tight" data-testid="current-term-name">{currentTerm.name}</div>
                  <div className="text-zinc-300 mt-2" data-testid="current-term-dates">{fmt(currentTerm.start_date)} — {fmt(currentTerm.end_date)}</div>
                  {enrolTerm?.id === currentTerm.id && enrolMeta && <div className="text-zinc-400 text-sm mt-1" data-testid="current-term-meta">{enrolMeta}</div>}
                  <div className="inline-block mt-3 text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-red-600 text-white" data-testid="enrolling-now-badge">Enrolling now</div>
                </>
              ) : (
                <div className="text-zinc-500 text-sm">No term is currently running.</div>
              )}
            </div>
            <div className="card-surface p-6">
              <div className="flex items-center gap-2 eyebrow text-zinc-400 mb-3"><Calendar size={14}/> Next term starts</div>
              {nextShown ? (
                <>
                  <div className="font-display text-2xl uppercase leading-tight" data-testid="next-term-name">{nextShown.name}</div>
                  <div className="text-zinc-300 mt-2" data-testid="next-term-start">{fmt(nextShown.start_date)}</div>
                  <div className="text-zinc-500 text-sm mt-1">{enrolTerm?.id === nextShown.id && enrolMeta ? enrolMeta : `Ends ${fmt(nextShown.end_date)}`}</div>
                </>
              ) : (
                <div className="text-zinc-500 text-sm">Next term dates coming soon.</div>
              )}
            </div>
          </div>
        )}
        {competition ? (
          <div className="card-surface p-12 text-center">
            <h2 className="font-display text-4xl uppercase">Selective. Audition-based.</h2>
            <p className="text-zinc-400 mt-4 max-w-xl mx-auto">Our Competition Team is a recruitment/audition track. Express your interest and we'll be in touch about the next assessment.</p>
            <Link to="/competition-team" className="btn-red mt-6 inline-flex">Apply / express interest</Link>
          </div>
        ) : (
          <>
          {enrolTerm && (
            <div className="flex items-center gap-3 mb-4" data-testid="enrol-term-heading">
              <span className="w-1 h-6 bg-red-600"/>
              <h2 className="font-display text-3xl uppercase">{enrolTerm.name}</h2>
              <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border border-red-600 text-red-500">{currentTerm ? "Current term · enrolling now" : "Next term · enrolling now"}</span>
            </div>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="enrol-sessions">
            {enrolSessions.length === 0 && <div className="col-span-full text-zinc-500 text-center py-20">No sessions available yet.</div>}
            {enrolSessions.map(s => {
              const full = s.availability_status === "full";
              return (
                <div key={s.id} className="card-surface p-6 card-hover flex flex-col" data-testid={`session-${s.id}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="font-display text-2xl">{s.day}</div>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 border ${STATUS_CLASS[s.availability_status]}`}>{STATUS_LABEL[s.availability_status]}</span>
                  </div>
                  <div className="space-y-2 text-sm text-zinc-300 flex-1">
                    <div className="flex items-center gap-2"><Clock size={14} className="text-red-500"/>{fmtRange(s.start_time, s.end_time)}</div>
                    <div className="flex items-center gap-2"><MapPin size={14} className="text-red-500"/>{s.location_name}</div>
                    <div className="flex items-center gap-2"><User size={14} className="text-red-500"/>{s.instructor}</div>
                  </div>
                  <div className="border-t border-white/10 mt-4 pt-4 flex flex-wrap justify-between items-center gap-3">
                    <div className="flex items-baseline gap-2">
                      <div className="font-bold text-lg" data-testid={`session-price-${s.id}`}>{Number(s.price) > 0 ? `$${Number(s.price).toFixed(0)}` : <span className="text-zinc-500 text-sm">Price TBA</span>}</div>
                      {s.price_per_class != null && s.schedule?.classes_count ? <div className="text-[10px] text-zinc-500">${s.price_per_class}/class × {s.schedule.classes_count}</div> : null}
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto [&>*]:flex-1 sm:[&>*]:flex-none [&>*]:justify-center [&>*]:whitespace-nowrap">
                      <a href={`${API}/sessions/${s.id}/calendar.ics`} title="Add to calendar" className="btn-ghost !py-2.5 !px-3 !text-xs flex items-center !flex-none min-h-[44px]" data-testid={`calendar-${s.id}`}><CalendarPlus size={13}/></a>
                      {trialOn && !full && (
                        <button onClick={() => setTrialOpen(s)} className={`${trialMode ? "btn-red" : "btn-ghost"} !py-2.5 !px-3 !text-xs min-h-[44px]`} data-testid={`trial-${s.id}`}>{trialMode ? "Book free trial" : "Free trial"}</button>
                      )}
                      {full ? (
                        <button onClick={() => setPreOpen(s)} className="btn-ghost !py-2.5 !px-3 !text-xs min-h-[44px]" data-testid={`pre-register-${s.id}`}>Pre-register</button>
                      ) : (
                        <button onClick={() => nav(`/register/${s.id}`)} className={`${trialMode && trialOn ? "btn-ghost" : "btn-red"} !py-2.5 !px-3 !text-xs min-h-[44px]`} data-testid={`register-${s.id}`}>Register</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {laterSessions.length > 0 && (
            <div className="mt-12 opacity-60" data-testid="later-term-sessions">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1 h-6 bg-zinc-600"/>
                <h2 className="font-display text-3xl uppercase text-zinc-300">{termName(laterSessions[0].term_id)}</h2>
                <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border border-zinc-600 text-zinc-400">Next term · not open yet</span>
              </div>
              <p className="text-zinc-500 text-sm mb-4">These are next term's classes for reference. Register above for the term running now.</p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {laterSessions.map(s => (
                  <div key={s.id} className="card-surface p-6 flex flex-col" data-testid={`later-session-${s.id}`}>
                    <div className="font-display text-2xl mb-3">{s.day}</div>
                    <div className="space-y-2 text-sm text-zinc-400">
                      <div className="flex items-center gap-2"><Clock size={14}/>{fmtRange(s.start_time, s.end_time)}</div>
                      <div className="flex items-center gap-2"><MapPin size={14}/>{s.location_name}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {preOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6" onClick={() => setPreOpen(null)}>
          <div className="card-surface p-8 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-3xl uppercase mb-2">Pre-register</h3>
            <p className="text-zinc-400 text-sm mb-6">Class is full — we'll contact you for the next intake.</p>
            {["full_name","email","phone","age","preferred_day"].map(k => (
              <input key={k} placeholder={k.replace("_"," ")} value={form[k]} onChange={e => setForm({...form,[k]: k === "phone" ? cleanPhone(e.target.value) : e.target.value})} className="w-full bg-black border border-white/10 px-4 py-3 mb-3 focus:border-red-600 outline-none" data-testid={`prereg-${k}`} />
            ))}
            <textarea placeholder="Notes" value={form.notes} onChange={e => setForm({...form,notes:e.target.value})} className="w-full bg-black border border-white/10 px-4 py-3 mb-4 focus:border-red-600 outline-none" rows={3} />
            <button onClick={submitPreReg} className="btn-red w-full" data-testid="submit-pre-reg">Submit</button>
          </div>
        </div>
      )}

      {trialOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6" onClick={() => setTrialOpen(null)}>
          <div className="card-surface p-8 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-3xl uppercase mb-2">Free trial class</h3>
            <p className="text-zinc-400 text-sm mb-2">{trialOpen.day} · {fmtTime(trialOpen.start_time)} · {trialOpen.location_name}</p>
            <p className="text-zinc-500 text-xs mb-6">No card required. Come try one class free.</p>
            {["full_name","parent_name","email","phone","age"].map(k => (
              <input key={k} placeholder={k.replace("_"," ")} value={trialForm[k]} onChange={e => setTrialForm({...trialForm,[k]: k === "phone" ? cleanPhone(e.target.value) : e.target.value})} className="w-full bg-black border border-white/10 px-4 py-3 mb-3 focus:border-red-600 outline-none" data-testid={`trial-${k}`} />
            ))}
            <textarea placeholder="Notes (allergies, medical, etc.)" value={trialForm.notes} onChange={e => setTrialForm({...trialForm,notes:e.target.value})} className="w-full bg-black border border-white/10 px-4 py-3 mb-4 focus:border-red-600 outline-none" rows={3} />
            <button onClick={submitTrial} disabled={!trialForm.full_name || !trialForm.email || !trialForm.phone} className="btn-red w-full disabled:opacity-50" data-testid="submit-trial">Book free trial</button>
          </div>
        </div>
      )}
    </>
  );
}
